import { reduce } from "lodash";

import {
  APPOINTMENT_STATUS_ARRIVED,
  APPOINTMENT_STATUS_CANCELED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_CONFIRMED,
  APPOINTMENT_STATUS_DRAFT,
  APPOINTMENT_STATUS_NO_SHOW,
  APPOINTMENT_STATUS_RESCHEDULED,
  APPOINTMENT_STATUS_UNCONFIRMED
} from "constants/appointments";
import EnumDataSource from "utils/enum-data-source.util";

export const getStatusTransition = (status: number) => {
  switch (status) {
    case APPOINTMENT_STATUS_DRAFT:
      return {
        label: "Move to Unconfirmed",
        status: APPOINTMENT_STATUS_UNCONFIRMED
      };
    case APPOINTMENT_STATUS_UNCONFIRMED:
      return {
        label: "Patient Confirmed",
        status: APPOINTMENT_STATUS_CONFIRMED
      };
    case APPOINTMENT_STATUS_CONFIRMED:
      return {
        label: "Patient Arrived",
        status: APPOINTMENT_STATUS_ARRIVED
      };
    case APPOINTMENT_STATUS_ARRIVED:
      return {
        label: "Complete",
        status: APPOINTMENT_STATUS_COMPLETED
      };
    case APPOINTMENT_STATUS_NO_SHOW:
      return {
        label: "Patient Arrived",
        status: APPOINTMENT_STATUS_ARRIVED
      };
    default:
      return null;
  }
};

export const STATUS_TRANSITION_OPTIONS = [
  {
    label: "Patient Confirmed",
    status: APPOINTMENT_STATUS_CONFIRMED,
    validTransitionsFor: [APPOINTMENT_STATUS_DRAFT]
  },
  {
    label: "Patient No-Show",
    status: APPOINTMENT_STATUS_NO_SHOW,
    validTransitionsFor: [
      APPOINTMENT_STATUS_UNCONFIRMED,
      APPOINTMENT_STATUS_CONFIRMED,
      APPOINTMENT_STATUS_ARRIVED
    ]
  },
  {
    label: "Patient Arrived",
    status: APPOINTMENT_STATUS_ARRIVED,
    validTransitionsFor: [APPOINTMENT_STATUS_UNCONFIRMED]
  },
  {
    label: "Reschedule",
    status: APPOINTMENT_STATUS_RESCHEDULED,
    validTransitionsFor: [
      APPOINTMENT_STATUS_UNCONFIRMED,
      APPOINTMENT_STATUS_CONFIRMED,
      APPOINTMENT_STATUS_ARRIVED,
      APPOINTMENT_STATUS_NO_SHOW,
      APPOINTMENT_STATUS_CANCELED
    ]
  },
  {
    label: "Cancelled",
    status: APPOINTMENT_STATUS_CANCELED,
    validTransitionsFor: [
      APPOINTMENT_STATUS_DRAFT,
      APPOINTMENT_STATUS_UNCONFIRMED,
      APPOINTMENT_STATUS_CONFIRMED,
      APPOINTMENT_STATUS_ARRIVED,
      APPOINTMENT_STATUS_NO_SHOW
    ]
  }
];

export const STATUS_CONFIRMATION_OPTIONS: {
  [statusCode: number]: { title: string; content: string };
} = {
  [APPOINTMENT_STATUS_UNCONFIRMED]: {
    title: "Are you sure you want to move this appointment to Unconfirmed?",
    content: "This change can not be undone. Do you still want to proceed?"
  },
  [APPOINTMENT_STATUS_CONFIRMED]: {
    title: "Are you sure you want mark this appointment Confirmed?",
    content: "This change can not be undone. Do you still want to proceed?"
  },
  [APPOINTMENT_STATUS_ARRIVED]: {
    title: "Are you sure you want mark this appointment Patient Arrived?",
    content: "This change can not be undone. Do you still want to proceed?"
  },
  [APPOINTMENT_STATUS_CANCELED]: {
    title: "Are you sure you want to cancel this appointment?",
    content: "This change can not be undone. Do you still want to proceed?"
  },
  [APPOINTMENT_STATUS_COMPLETED]: {
    title: "Are you sure you want to complete this appointment?",
    content: "This change can not be undone. Do you still want to proceed?"
  },
  [APPOINTMENT_STATUS_NO_SHOW]: {
    title: "Are you sure you want to mark this appointment no-show?",
    content: "This change can not be undone. Do you still want to proceed?"
  },
  [APPOINTMENT_STATUS_RESCHEDULED]: {
    title: "Are you sure you want to reschedule this appointment?",
    content: "This change can not be undone. Do you still want to proceed?"
  }
};

export const isDraftPendingPatientVerification = (
  status: number,
  hasPatient: boolean
) => status === APPOINTMENT_STATUS_DRAFT && !hasPatient;

export const getAvailableStatusTransitions = (
  status: number,
  hasPatient = true
) => {
  const availableStatusTransitions = isDraftPendingPatientVerification(
    status,
    hasPatient
  )
    ? []
    : [
        getStatusTransition(status),
        ...STATUS_TRANSITION_OPTIONS.filter(({ validTransitionsFor }) =>
          validTransitionsFor.includes(status)
        )
      ].filter((option) => option);

  return new EnumDataSource(
    reduce(
      availableStatusTransitions,
      (acc: any, cur: any) => ({
        ...acc,
        [cur.status]: cur.label
      }),
      {}
    )
  );
};
